/**
 * Free Gift Manager
 *
 * Automatically manages free gift promotions based on cart contents.
 * Uses metaobject-based promo configuration for centralised control.
 *
 */

class FreeGiftManager {
  constructor() {
    // Guards against re-entrant evaluation when a cart update fires mid-processing.
    this.isProcessing = false;
    // Keyed by trigger product ID for fast lookup; universal promos (no trigger ID) are not cached here.
    this.promoDataCache = new Map();
    this.promoData = [];
    // Underscore prefix hides these line item properties from the Shopify cart UI.
    this.giftPropertyKey = '_is_free_gift';
    this.promoIdPropertyKey = '_promo_id';

    this.init();
  }

  init() {
    this.loadPromoData();
    this.setupEventListeners();
    this.evaluateCart();
  }

  loadPromoData() {
    const promoDataElement = document.getElementById('free-gift-promo-data');

    if (!promoDataElement) {
      this.promoData = [];
      this.promoDataCache.clear();
      return;
    }
    // Cache product-specific promos by trigger product ID for quick lookups.
    // Promos with no trigger_product_ids are universal and handled in identifyTriggerProducts.
    this.parseAndCachePromoData(promoDataElement.textContent, 'initial load');
  }

  parseAndCachePromoData(jsonString, source = 'unknown') {
    this.promoDataCache.clear();

    try {
      this.promoData = JSON.parse(jsonString);

      this.promoData.forEach(promo => {
        const triggerId = promo.trigger_product_ids;
        if (!triggerId || triggerId === '') return;

        const pid = triggerId.toString();
        if (!this.promoDataCache.has(pid)) {
          this.promoDataCache.set(pid, []);
        }
        this.promoDataCache.get(pid).push(promo);
      });

    } catch (e) {
      console.error('[FreeGiftManager]', 'Failed to parse promo data:', e);
      this.promoData = [];
    }
  }

  async fetchFreshPromoData() {
    try {
      // Re-renders the cart-drawer section server-side to get fresh promo config from the free-gift-promo-data snippet.
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const promoDataElement = doc.getElementById('free-gift-promo-data');

      if (promoDataElement) {
        this.parseAndCachePromoData(promoDataElement.textContent, 'cart update fetch');

        const domElement = document.getElementById('free-gift-promo-data');
        if (domElement) {
          domElement.textContent = promoDataElement.textContent;
        }
      } else {
        this.promoData = [];
        this.promoDataCache.clear();
      }
    } catch (e) {
      console.error('[FreeGiftManager]', 'Failed to fetch fresh promo data:', e);
    }
  }

  setupEventListeners() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'free-gift-manager') {
          return;
        }
        this.evaluateCart();
      });
    } else {
      // Fall back to polling on pages where pubsub.js hasn't been loaded.
      this.startPolling();
    }

    // Re-evaluate when the user returns to the tab in case the cart changed in another tab.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.evaluateCart();
      }
    });
  }

  startPolling() {
    this.lastCartToken = null;

    setInterval(async () => {
      try {
        const cart = await this.fetchCart();
        if (cart.token !== this.lastCartToken) {
          this.lastCartToken = cart.token;
          this.evaluateCartWithData(cart);
        }
      } catch (e) {
        console.error('[FreeGiftManager]', 'Polling error:', e);
      }
    }, 2000);
  }

  async fetchCart() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      console.error('[FreeGiftManager]', 'Failed to fetch cart:', e);
      throw e;
    }
  }

  async evaluateCart() {
    if (this.isProcessing) {
      return;
    }

    try {
      await this.fetchFreshPromoData();
      const cart = await this.fetchCart();
      await this.evaluateCartWithData(cart);
    } catch (e) {
      console.error('[FreeGiftManager]', 'Failed to evaluate cart:', e);
    }
  }

  async evaluateCartWithData(cart) {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const currentGifts = this.identifyGiftsInCart(cart);
      const triggerProducts = this.identifyTriggerProducts(cart);
      const requiredGifts = this.calculateRequiredGifts(triggerProducts);
      const giftsToRemove = this.getGiftsToRemove(currentGifts, requiredGifts);
      const giftsToAdd = this.getGiftsToAdd(currentGifts, requiredGifts);

      if (giftsToRemove.length > 0) {
        await this.removeGiftsFromCart(giftsToRemove);
      }

      if (giftsToAdd.length > 0) {
        await this.addGiftsToCart(giftsToAdd);
      }

      await this.syncGiftQuantities(cart, requiredGifts);

    } catch (e) {
      console.error('[FreeGiftManager]', 'Error during cart evaluation:', e);
    } finally {
      this.isProcessing = false;
    }
  }

  isPromoActive(promo) {
    if (promo.enabled !== true && promo.enabled !== 'true') {
      return false;
    }

    const now = new Date();

    if (promo.start_date && promo.start_date !== '') {
      // Append local midnight so date comparison uses the store's timezone, not UTC.
      const startDate = new Date(promo.start_date + 'T00:00:00');
      if (now < startDate) {
        return false;
      }
    }

    if (promo.end_date && promo.end_date !== '') {
      const endDate = new Date(promo.end_date + 'T23:59:59');
      if (now > endDate) {
        return false;
      }
    }

    return true;
  }

  identifyGiftsInCart(cart) {
    return cart.items.filter(item => {
      return item.properties && item.properties[this.giftPropertyKey] === 'true';
    }).map(item => ({
      key: item.key,
      variantId: item.variant_id,
      quantity: item.quantity,
      promoId: item.properties ? item.properties[this.promoIdPropertyKey] : null,
      lineIndex: cart.items.indexOf(item) + 1
    }));
  }
  
  /**
   * Identify trigger products in the cart that qualify for gifts.
   * Loops all promos directly — universal promos (no trigger_product_ids) fire when
   * any non-gift items meet the min quantity; product-specific promos fire when the
   * matching product is in the cart.
   */
  
  identifyTriggerProducts(cart) {
    const triggers = [];

    // Pre-computed once so universal promos don't re-sum cart items on every iteration.
    let totalNonGiftQuantity = 0;
    cart.items.forEach(item => {
      if (item.properties && item.properties[this.giftPropertyKey] === 'true') return;
      totalNonGiftQuantity += item.quantity;
    });

    this.promoData.forEach(promo => {
      if (!this.isPromoActive(promo)) return;

      const minQty = promo.min_trigger_quantity || 1;
      const triggerId = promo.trigger_product_ids;

      if (!triggerId || triggerId === '') {
        if (totalNonGiftQuantity >= minQty) {
          triggers.push({
            productId: 'global',
            variantId: null,
            quantity: totalNonGiftQuantity,
            promo: promo,
            lineKey: null
          });
        }
      } else {
        cart.items.forEach(item => {
          if (item.properties && item.properties[this.giftPropertyKey] === 'true') return;

          if (item.product_id.toString() === triggerId.toString()) {
            if (item.quantity >= minQty) {
              triggers.push({
                productId: item.product_id.toString(),
                variantId: item.variant_id,
                quantity: item.quantity,
                promo: promo,
                lineKey: item.key
              });
            }
          }
        });
      }
    });

    return triggers;
  }

  calculateRequiredGifts(triggerProducts) {
    const requiredGifts = new Map();

    triggerProducts.forEach(trigger => {
      const promo = trigger.promo;
      const giftVariantId = promo.gift_variant_id;
      const promoId = promo.id;

      // Metaobject fields serialise as the string 'null' when blank.
      if (!giftVariantId || giftVariantId === '' || giftVariantId === 'null') {
        return;
      }

      if (promo.auto_add === false || promo.auto_add === 'false') {
        return;
      }

      let giftQuantity = promo.gift_quantity || 1;

      if (promo.quantity_match) {
        const minQty = promo.min_trigger_quantity || 1;
        giftQuantity = Math.floor(trigger.quantity / minQty) * giftQuantity;
      }

      // Composite key deduplicates the same gift triggered by multiple cart items; keeps the highest quantity.
      const key = `${giftVariantId}-${promoId}`;
      if (requiredGifts.has(key)) {
        const existing = requiredGifts.get(key);
        existing.quantity = Math.max(existing.quantity, giftQuantity);
      } else {
        requiredGifts.set(key, {
          variantId: giftVariantId,
          quantity: giftQuantity,
          promoId: promoId,
          promoName: promo.name
        });
      }
    });

    return Array.from(requiredGifts.values());
  }

  getGiftsToRemove(currentGifts, requiredGifts) {
    return currentGifts.filter(currentGift => {
      const stillRequired = requiredGifts.some(required =>
        required.variantId.toString() === currentGift.variantId.toString() &&
        required.promoId === currentGift.promoId
      );
      return !stillRequired;
    });
  }

  getGiftsToAdd(currentGifts, requiredGifts) {
    return requiredGifts.filter(required => {
      const alreadyInCart = currentGifts.some(current =>
        current.variantId.toString() === required.variantId.toString() &&
        current.promoId === required.promoId
      );
      return !alreadyInCart;
    });
  }

  async removeGiftsFromCart(giftsToRemove) {
    for (const gift of giftsToRemove) {
      try {
        await this.updateCartItem(gift.key, 0); // quantity 0 removes the line item via cart/change.js
        this.showGiftRemovedNotification(gift);
      } catch (e) {
        console.error('[FreeGiftManager]', `Failed to remove gift variant ${gift.variantId}:`, e);
      }
    }

    this.publishCartUpdate();
  }

  async addGiftsToCart(giftsToAdd) {
    for (const gift of giftsToAdd) {
      try {
        await this.addItemToCart(gift.variantId, gift.quantity, {
          [this.giftPropertyKey]: 'true',
          [this.promoIdPropertyKey]: gift.promoId,
          '_removeable': `_${gift.variantId}-gift`
        }, `_${gift.variantId}-gift`);
        this.showGiftAddedNotification(gift);
      } catch (e) {
        console.error('[FreeGiftManager]', `Failed to add gift variant ${gift.variantId}:`, e);
        if (e.message && e.message.includes('not available')) {
          this.showGiftUnavailableNotification(gift);
        }
      }
    }

    this.publishCartUpdate();
  }

  async syncGiftQuantities(cart, requiredGifts) {
    const currentGifts = this.identifyGiftsInCart(cart);

    for (const required of requiredGifts) {
      const currentGift = currentGifts.find(g =>
        g.variantId.toString() === required.variantId.toString() &&
        g.promoId === required.promoId
      );

      if (currentGift && currentGift.quantity !== required.quantity) {
        try {
          await this.updateCartItem(currentGift.key, required.quantity);
        } catch (e) {
          console.error('[FreeGiftManager]', 'Failed to sync gift quantity:', e);
        }
      }
    }
  }

  async addItemToCart(variantId, quantity, properties = {}, giftPromoId = null) {
    const body = JSON.stringify({
      id: variantId,
      quantity: quantity,
      properties: properties
    });

    const response = await fetch(routes.cart_add_url, {
      ...this.fetchConfig(),
      body: body
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add item to cart');
    }

    return await response.json();
  }

  async updateCartItem(lineKey, quantity) {
    const body = JSON.stringify({
      id: lineKey,
      quantity: quantity
    });

    const response = await fetch(routes.cart_change_url, {
      ...this.fetchConfig(),
      body: body
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to update cart item');
    }

    return await response.json();
  }

  fetchConfig() {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
  }

  publishCartUpdate() {
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-manager' });
    }
  }

  // Notification strings are set by free-gift-promo-data.liquid via window.freeGiftStrings.
  showGiftAddedNotification(gift) {
    if (typeof window.showNotification === 'function') {
      window.showNotification({
        type: 'success',
        message: window.freeGiftStrings?.giftAdded || ''
      });
    }
  }

  showGiftRemovedNotification(gift) {
    if (typeof window.showNotification === 'function') {
      window.showNotification({
        type: 'info',
        message: window.freeGiftStrings?.giftRemoved || ''
      });
    }
  }

  showGiftUnavailableNotification(gift) {
    if (typeof window.showNotification === 'function') {
      window.showNotification({
        type: 'warning',
        message: window.freeGiftStrings?.giftUnavailable || ''
      });
    }
  }

  async removeAllGifts() {
    const cart = await this.fetchCart();
    const gifts = this.identifyGiftsInCart(cart);
    await this.removeGiftsFromCart(gifts);
  }

  async refreshPromoData() {
    await this.fetchFreshPromoData();
    this.evaluateCart();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('free-gift-promo-data') ||
      window.location.pathname.includes('/cart') ||
      document.querySelector('cart-drawer')) {
    window.freeGiftManager = new FreeGiftManager();
  }
});

// Handles the case where this script loads after DOMContentLoaded has already fired.
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(() => {
    if (!window.freeGiftManager &&
        (document.getElementById('free-gift-promo-data') ||
         window.location.pathname.includes('/cart') ||
         document.querySelector('cart-drawer'))) {
      window.freeGiftManager = new FreeGiftManager();
    }
  }, 100);
}
